import React from "react";
import { formatDate, timeAgo } from "@/lib/api";

/**
 * Subscriptions tab: membership list, recorded payments and checkout attempts (read-only).
 *
 * Extracted verbatim from src/pages/Admin.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged - only the file
 * boundary moved.
 */
export default function AdminSubscriptions({ subs }) {
  return (
        <div className="mt-6 space-y-6" data-testid="admin-subscriptions">
          <div className="sm-card bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F4EF] text-left">
                <tr>
                  {["Member", "Email", "Plan", "Price", "Status", "Renews / ends"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-bold text-[#4B5A60] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DED4]">
                {subs.subscriptions.map((s) => (
                  <tr key={s.user_id}>
                    <td className="px-3 py-2.5 font-semibold text-[#1F2A2E]">{s.username}</td>
                    <td className="px-3 py-2.5 text-[#4B5A60]">{s.email}</td>
                    <td className="px-3 py-2.5">{s.plan_label || "-"}</td>
                    <td className="px-3 py-2.5">{s.price || "-"}</td>
                    <td className="px-3 py-2.5">
                      {s.status}
                      {s.cancel_at_period_end ? " (cancels)" : ""}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{formatDate(s.end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {subs.subscriptions.length === 0 && (
              <p className="px-4 py-6 text-sm text-[#4B5A60]">No subscriptions yet.</p>
            )}
          </div>

          <div className="sm-card bg-white overflow-x-auto">
            <h3 className="px-4 pt-4 font-display text-lg font-bold text-[#1F2A2E]">
              Payments received (includes renewals)
            </h3>
            <table className="mt-2 w-full text-sm">
              <thead className="bg-[#F7F4EF] text-left">
                <tr>
                  {["Invoice", "Plan", "Amount", "Status", "Reason", "Date"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-bold text-[#4B5A60]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DED4]">
                {subs.payments.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2.5">
                      {p.hosted_invoice_url ? (
                        <a
                          href={p.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#1F6F78] underline"
                        >
                          {p.invoice_number || p.id}
                        </a>
                      ) : (
                        p.invoice_number || p.id
                      )}
                    </td>
                    <td className="px-3 py-2.5">{p.lookup_key || "-"}</td>
                    <td className="px-3 py-2.5">${Number(p.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          p.status === "paid"
                            ? "bg-[#E4F3EA] text-[#14603C]"
                            : "bg-[#FBE6E4] text-[#B42318]"
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[#4B5A60]">{p.billing_reason || "-"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{timeAgo(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {subs.payments.length === 0 && (
              <p className="px-4 py-6 text-sm text-[#4B5A60]">
                No payments recorded yet. Successful charges and renewals land here automatically
                once Stripe sends an invoice event.
              </p>
            )}
          </div>

          <div className="sm-card bg-white overflow-x-auto">
            <h3 className="px-4 pt-4 font-display text-lg font-bold text-[#1F2A2E]">
              Recent checkout attempts
            </h3>
            <table className="mt-2 w-full text-sm">
              <thead className="bg-[#F7F4EF] text-left">
                <tr>
                  {["Plan", "Amount", "Status", "Payment", "Created"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-bold text-[#4B5A60]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DED4]">
                {(subs.checkout_attempts || []).map((p) => (
                  <tr key={p.session_id}>
                    <td className="px-3 py-2.5">{p.lookup_key}</td>
                    <td className="px-3 py-2.5">${Number(p.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2.5">{p.status}</td>
                    <td className="px-3 py-2.5">{p.payment_status}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{timeAgo(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(subs.checkout_attempts || []).length === 0 && (
              <p className="px-4 py-6 text-sm text-[#4B5A60]">No checkout attempts yet.</p>
            )}
          </div>
        </div>
  );
}
